import { Link } from 'react-router-dom';
import { Building2, Wrench, Droplets, HardHat, ArrowRight, Zap, Shield, Paintbrush, Search, Home, Plane, Droplet } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services } from '../mock';

const Services = () => {
  const iconMap = {
    Building2: Building2,
    Wrench: Wrench,
    Droplets: Droplets,
    HardHat: HardHat,
    Zap: Zap,
    Shield: Shield,
    Paintbrush: Paintbrush,
    Search: Search,
    Home: Home,
    Drone: Plane,
    Droplet: Droplet
  };

  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white hover:bg-white/30">
              Nossos Serviços
            </Badge>
            <h1 className="text-5xl font-bold">
              Soluções Completas para Seu Condomínio
            </h1>
            <p className="text-xl text-blue-100">
              Serviços especializados em reforma e manutenção predial com excelência técnica e compromisso com resultados.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Building2;
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-300">
                  <CardContent className="p-8 space-y-6">
                    <div className="flex items-start justify-between">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                        <Icon size={32} />
                      </div>
                      {service.featured && (
                        <Badge className="bg-[#F47A20] hover:bg-[#e66a10] text-white">
                          Destaque
                        </Badge>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#15448A] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">O que está incluído:</h4>
                      <ul className="space-y-2">
                        {service.details.map((detail, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                            <span className="text-[#15448A] mt-0.5">✓</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {service.featured && (
                      <Link to="/reforma-fachada">
                        <Button className="w-full bg-gradient-to-r from-[#F47A20] to-[#e66a10] hover:from-[#e66a10] hover:to-[#d45d09] text-white">
                          Saiba Mais
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#15448A]/10 text-[#15448A] hover:bg-[#15448A]/20 mb-4">
              Nosso Processo
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Como Trabalhamos</h2>
            <p className="text-lg text-gray-600">
              Um processo estruturado e transparente para garantir o sucesso do seu projeto.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border-2 rounded-xl px-6">
                <AccordionTrigger className="text-lg font-semibold hover:text-[#15448A]">
                  1. Vistoria Técnica e Diagnóstico
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pt-4">
                  <p className="mb-4">
                    Nossa equipe técnica realiza uma inspeção completa e detalhada do imóvel, identificando todos os pontos que necessitam de atenção.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Análise estrutural completa</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Identificação de patologias e problemas</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Registro fotográfico detalhado</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Laudo técnico preliminar</span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white border-2 rounded-xl px-6">
                <AccordionTrigger className="text-lg font-semibold hover:text-[#15448A]">
                  2. Elaboração de Proposta e Orçamento
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pt-4">
                  <p className="mb-4">
                    Baseado na vistoria, elaboramos uma proposta completa com orçamento detalhado e transparente.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Escopo detalhado de serviços</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Especificação de materiais</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Cronograma de execução</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Condições de pagamento flexíveis</span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white border-2 rounded-xl px-6">
                <AccordionTrigger className="text-lg font-semibold hover:text-[#15448A]">
                  3. Planejamento e Aprovações
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pt-4">
                  <p className="mb-4">
                    Apoiamos o condomínio em todo o processo de aprovação e planejamento da obra.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Material para apresentação em assembleia</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Documentação técnica completa</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Obtenção de licenças necessárias</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Planejamento logístico detalhado</span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white border-2 rounded-xl px-6">
                <AccordionTrigger className="text-lg font-semibold hover:text-[#15448A]">
                  4. Execução da Obra
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pt-4">
                  <p className="mb-4">
                    Execução com equipe qualificada, segurança total e comunicação constante.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Equipe técnica especializada e certificada</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Cumprimento rigoroso de normas de segurança</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Relatórios periódicos de progresso</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Gestão de cronograma e qualidade</span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white border-2 rounded-xl px-6">
                <AccordionTrigger className="text-lg font-semibold hover:text-[#15448A]">
                  5. Entrega e Garantia
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pt-4">
                  <p className="mb-4">
                    Entrega formal com toda documentação e garantia estendida dos serviços.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Vistoria final detalhada</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Entrega de toda documentação técnica</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Manual de manutenção</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#15448A]">✓</span>
                      <span>Garantia de 2 a 5 anos conforme serviço</span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">
              Pronto para Começar Seu Projeto?
            </h2>
            <p className="text-xl text-blue-100">
              Solicite uma vistoria técnica gratuita e receba uma proposta personalizada.
            </p>
            <Link to="/contato">
              <Button size="lg" className="bg-white text-[#15448A] hover:bg-gray-100 shadow-xl">
                Solicitar Orçamento Gratuito
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;